<script setup>
/**
 * Account-level travel search defaults.
 *
 * The content-source control is the dangerous one and is treated as such. Turning NDC
 * off does not "narrow results" - it makes AA, UA, QF and SQ NDC inventory invisible,
 * and an agent looking at the list has no way to tell. So deselecting anything raises a
 * persistent warning naming the consequence, and the last source cannot be removed:
 * an empty list is not "no preference", it is a search that returns nothing.
 *
 * There is no currency control on purpose. The traced search request has no currency
 * field, the quoting currency comes from the connection's PCC, and Travel::Money refuses
 * cross-currency arithmetic - a selector here would be a knob that silently does nothing.
 */
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import TravelPreferencesAPI from 'dashboard/api/travel/preferences';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const { t } = useI18n();

const CONTENT_SOURCES = ['GDS', 'NDC'];
const UPSELL_OPTIONS = [0, 1, 2, 3].map(value => ({
  value,
  label: String(value),
}));

const contentSources = ref([...CONTENT_SOURCES]);
const maxUpsells = ref(0);
const isLoading = ref(false);
const isSaving = ref(false);

const isSourceExcluded = computed(
  () => contentSources.value.length < CONTENT_SOURCES.length
);

// The last remaining source is disabled rather than silently refused: the schema rejects
// an empty list, and a zero-source search returns nothing at all.
const isLastSource = source =>
  contentSources.value.length === 1 && contentSources.value.includes(source);

const toggleSource = source => {
  if (contentSources.value.includes(source)) {
    contentSources.value = contentSources.value.filter(item => item !== source);
  } else {
    // Rebuilt from CONTENT_SOURCES so the stored order is stable regardless of the
    // order the boxes were clicked in.
    contentSources.value = CONTENT_SOURCES.filter(
      item => item === source || contentSources.value.includes(item)
    );
  }
};

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await TravelPreferencesAPI.get();
    contentSources.value = data.preferences.content_sources;
    maxUpsells.value = data.preferences.max_upsells;
  } catch (error) {
    useAlert(error?.response?.data?.error || t('TRAVEL.PREFERENCES.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    await TravelPreferencesAPI.update({
      content_sources: contentSources.value,
      max_upsells: Number(maxUpsells.value),
    });
    useAlert(t('TRAVEL.PREFERENCES.SAVED'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('TRAVEL.PREFERENCES.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="w-full max-w-3xl px-6 py-6 mx-auto">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('TRAVEL.PREFERENCES.TITLE') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('TRAVEL.PREFERENCES.SUBTITLE') }}
      </p>

      <div class="p-4 mt-5 border rounded-xl border-n-weak bg-n-solid-1">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('TRAVEL.PREFERENCES.CONTENT_SOURCES.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('TRAVEL.PREFERENCES.CONTENT_SOURCES.HELP') }}
        </p>

        <div class="flex gap-6 mt-3">
          <label
            v-for="source in CONTENT_SOURCES"
            :key="source"
            class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-12"
          >
            <Checkbox
              :model-value="contentSources.includes(source)"
              :disabled="isLastSource(source)"
              @change="toggleSource(source)"
            />
            {{ source }}
          </label>
        </div>

        <div
          v-if="isSourceExcluded"
          class="p-3 mt-3 text-sm border rounded-lg border-n-amber-6 bg-n-amber-2 text-n-amber-11"
        >
          {{ t('TRAVEL.PREFERENCES.CONTENT_SOURCES.WARNING') }}
        </div>
      </div>

      <div class="p-4 mt-4 border rounded-xl border-n-weak bg-n-solid-1">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('TRAVEL.PREFERENCES.UPSELLS.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('TRAVEL.PREFERENCES.UPSELLS.HELP') }}
        </p>
        <div class="mt-3">
          <Select v-model="maxUpsells" :options="UPSELL_OPTIONS" />
        </div>
      </div>

      <p class="mt-4 text-xs text-n-slate-10">
        {{ t('TRAVEL.PREFERENCES.CURRENCY_NOTE') }}
      </p>

      <div class="flex justify-end mt-5">
        <Button
          :label="t('TRAVEL.PREFERENCES.SAVE')"
          :is-loading="isSaving"
          :disabled="isLoading"
          @click="save"
        />
      </div>
    </div>
  </div>
</template>
